import type {
  HandleTreeMapSubFilterProps,
  TreeMapContextData,
  TreeMapFilterByType,
  TreeMapFilterType,
  TreeMapProps,
  TreeMapSubFiltersCollection,
  TreeMapSubFilterType,
  UseTreeMapAssetsByValueProps,
} from "./tree-map.type";
import type { AssetState } from "@/entities/asset";
import type {
  AssetTreeNodeAsset,
  AssetTreeNodeLocation,
} from "@/entities/asset-tree";
import type { ReferentialConditionRatingValue } from "@/entities/referential/referential-condition-ratings";

import { useMemo, useState } from "react";

import { useGetTree } from "@/entities/asset-tree";
import { useTreeNodeMarker } from "@/modules/asset-tree/asset-tree-node-marker";
import { isKeyOfObject } from "@/utilities/object";

import { TREE_MAP_DEFAULT_FILTER_BY_TYPE } from "./tree-map.constant";
import { useTreeMap } from "./tree-map.context";
import {
  applyTreeMapNodesSelector,
  getTreeEndpoint,
  getTreeMapGeojsonOptions,
  getTreeMapNodesByValue,
  getTreeMapStats,
  hideAssetsFilters,
  hideLocationsFilters,
  isTreeMapNodeActive,
  transformTreeMapSubFilters,
} from "./tree-map.helper";

/**
 * Hook used to defined TreeMap filters context values
 * -> use it only inside `TreeMapProvider`
 */
export const useTreeMapContextData = ({
  type,
  id,
  loading,
  mapKey,
  subItemsDepth,
}: TreeMapProps): TreeMapContextData => {
  const endpoint = getTreeEndpoint(type);
  const { tree, isLoading } = useGetTree({
    id: id ?? 0,
    endpoint,
  });
  const { getMarker } = useTreeNodeMarker();

  const { filterByType, toggleFilterByType, setFilterByType } =
    useTreeMapContextFilterByType();
  const { subFilters, toggleSubFilter, activateSubFilter, resetSubFilter } =
    useTreeMapContextSubFilters();

  const { nodesByType, markers, geojson, hasGeojson } = useMemo(
    () =>
      applyTreeMapNodesSelector({
        nodes: tree,
        filterByType,
        subFilters,
        getMarker,
      }),
    [tree, filterByType, subFilters, getMarker]
  );

  const stats = getTreeMapStats({ nodesByType, hasGeojson });

  const showLocationsFilters = !hideLocationsFilters(stats);
  const showAssetsFilters = !hideAssetsFilters(stats);
  const showFilters = showLocationsFilters || showAssetsFilters;

  const geojsonOptions = getTreeMapGeojsonOptions(subFilters);

  return {
    nodesByType,
    markers,
    geojson,
    geojsonOptions,
    loading: loading || isLoading,
    mapId: `${mapKey}_${id ?? "no-id"}`,
    subItemsDepth,
    filterByType,
    toggleFilterByType,
    setFilterByType,
    subFilters,
    toggleSubFilter,
    activateSubFilter,
    resetSubFilter,
    showFilters,
    showLocationsFilters,
    showAssetsFilters,
    stats,
  };
};

/**
 * Hook used to defined TreeMap `filterByType` context values
 * -> use it only inside `TreeMapProvider`
 */
export const useTreeMapContextFilterByType = () => {
  const [filter, setFilter] = useState<TreeMapFilterByType>(
    TREE_MAP_DEFAULT_FILTER_BY_TYPE
  );

  const toggleFilter = (type: TreeMapFilterType) => {
    setFilter((filter) => ({ ...filter, [type]: !filter[type] }));
  };

  const setFilterByType = (type: TreeMapFilterType, value: boolean) => {
    setFilter((filter) => ({ ...filter, [type]: value }));
  };

  return {
    filterByType: filter,
    toggleFilterByType: toggleFilter,
    setFilterByType,
  };
};

/**
 * Hook used to defined TreeMap `subFilters` context values
 * -> use it only inside `TreeMapProvider`
 */
export const useTreeMapContextSubFilters = () => {
  const [filters, setFilters] = useState<TreeMapSubFiltersCollection>({
    location: { position: true, perimeter: true },
  });

  const toggleSubFilter = ({ type, key }: HandleTreeMapSubFilterProps) => {
    setFilters((filters) => {
      const filterValues = filters[type] ?? {};
      const newValue = isKeyOfObject(filterValues, key)
        ? !filterValues[key]
        : true;

      return {
        ...filters,
        [type]: { ...filters[type], [key]: newValue },
      };
    });
  };

  const activateSubFilter = ({ type, key }: HandleTreeMapSubFilterProps) => {
    setFilters((filters) => ({
      ...filters,
      [type]: { ...filters[type], [key]: true },
    }));
  };

  const resetSubFilter = (type: TreeMapSubFilterType) => {
    setFilters((filters) => ({ ...filters, [type]: {} }));
  };

  return {
    subFilters: transformTreeMapSubFilters(filters),
    activateSubFilter,
    toggleSubFilter,
    resetSubFilter,
  };
};

export const useTreeMapActiveSectors = () => {
  const { nodesByType } = useTreeMap();

  return (nodesByType.sector ?? [])
    .filter(isTreeMapNodeActive)
    .map((node) => node.node as AssetTreeNodeLocation);
};

export const useTreeMapAssets = () => {
  const { nodesByType } = useTreeMap();

  return (nodesByType.asset ?? []).map(
    (node) => node.node as AssetTreeNodeAsset
  );
};

export const useTreeMapActiveAssets = () => {
  const { nodesByType } = useTreeMap();

  return (nodesByType.asset ?? [])
    .filter(isTreeMapNodeActive)
    .map((node) => node.node as AssetTreeNodeAsset);
};

export const useTreeMapAssetsByValue = <
  Value extends ReferentialConditionRatingValue | AssetState,
>({
  getTreeNodeValue,
}: UseTreeMapAssetsByValueProps<Value>) => {
  const { nodesByType } = useTreeMap();

  const assetsByValue = useMemo(() => {
    const nodes = nodesByType.asset ?? [];
    return getTreeMapNodesByValue({ nodes, getTreeNodeValue });
  }, [nodesByType, getTreeNodeValue]);

  const getAssetsCount = (value: Value) => {
    return assetsByValue[value]?.length ?? 0;
  };

  const getActiveAssetsCount = (value: Value) => {
    return (assetsByValue[value] ?? []).filter(isTreeMapNodeActive).length;
  };

  return {
    getAssetsCount,
    getActiveAssetsCount,
  };
};
